import type { ArticleType } from "@/modules/companies/constants/enums"
import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"
import Image from "next/image"
import { useParams } from "next/navigation"
import { useState } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { Heading } from "@jobteaser/spark/components/Heading"
import { Briefcase } from "@jobteaser/spark/components/Icons/Briefcase"
import { Text } from "@jobteaser/spark/components/Text"
import { ElementGrid } from "@jobteaser/ui-kit/components/ElementGrid/ElementGrid"
import { Layout, LAYOUT_COLOR_BLOCK } from "@jobteaser/ui-kit/components/Layout/Layout"
import { BackLink } from "@jobteaser/ui-kit/components/Link/BackLink"
import { Link as LinkUiKit } from "@jobteaser/ui-kit/components/Link/Link"
import { Modal } from "@jobteaser/ui-kit/components/Modal/Modal"
import { RichText } from "@jobteaser/ui-kit/components/RichText/RichText"
import { Signature } from "@jobteaser/ui-kit/components/Signature/Signature"
import { Tag } from "@jobteaser/ui-kit/components/Tag/Tag"

import { ARTICLE_TITLE_ID } from "@/features/companyDetail/components/NewsFeed/ArticlesTitle/ArticlesTitle"
import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { ARTICLE_SUBJECT_TYPES_I18N } from "@/modules/companies/constants/enums"
import { useCompanyArticleDetailsFetch } from "@/modules/companies/hooks/useCompanyArticleDetailsFetch"
import { useCompanyFetch } from "@/modules/companies/hooks/useCompanyFetch"
import { COMPANY_BASENAME, NEWSFEED_PAGE_ROUTE } from "@/modules/companies/utils/routing"
import { DATE_FORMATS, getDate } from "@/modules/dates/dates"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useJobAdsFromAlgolia } from "@/modules/jobAds/client/hooks/useJobAdsFromAlgolia"
import { useLocale } from "@/modules/locales/useLocale"
import { Link } from "@/modules/routing/components/Link"
import { useTracking } from "@/modules/tracking/useTracking"

import styles from "./ArticleDetails.module.css"

const VideoDynamic = dynamic(() =>
  import("@/features/companyDetail/components/shared/Media/Video").then(mod => mod.MediaVideo)
)

const ImageDynamic = dynamic(() =>
  import("@/features/companyDetail/components/shared/Media/Image").then(mod => mod.MediaImage)
)

type CtaPosition = "top" | "bottom"

export const ArticleDetails: FunctionComponent = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalImageSrc, setModalImageSrc] = useState("")
  const { t } = useTranslation(["companies_client"])
  const { dataCompanyArticleDetails } = useCompanyArticleDetailsFetch()
  const { dataJobAds } = useJobAdsFromAlgolia()
  const { dataCareerCenter } = useCareerCenterFetch()
  const { dataCompany } = useCompanyFetch()
  const { companySlug } = useParams()
  const locale = useLocale()
  const tracking = useTracking()

  if (!dataCompanyArticleDetails) return null

  const openModal = (src: string): void => {
    setIsModalOpen(true)
    setModalImageSrc(src)
  }

  const closeModal = (): void => {
    setIsModalOpen(false)
    setModalImageSrc("")
  }

  const handleClick = (position: CtaPosition): void => {
    tracking?.track("companies_push_com_view_offers_click", {
      position,
      service: "companies",
    })
  }

  const { title, content, createTime, images, video, externUrl, subject, targeted, boosted } =
    dataCompanyArticleDetails.article

  const articleType = ARTICLE_SUBJECT_TYPES_I18N[subject] as ArticleType

  const hasVideo = !!video
  const hasImage = !hasVideo && images && images.length > 0
  const hasGallery = (!hasVideo && images && images.length > 1) || (hasVideo && images && images.length > 0)
  const nbJobAds = dataJobAds?.totalSize || 0
  const isRecruitmentArticleWithJobs =
    boosted && nbJobAds > 0 && articleType === ARTICLE_SUBJECT_TYPES_I18N.ARTICLE_SUBJECT_RECRUITMENT

  return (
    <Layout isSmall colorBlock={LAYOUT_COLOR_BLOCK.WHITE}>
      <Layout.Column>
        <BackLink
          component={Link}
          href={`/${locale}/${COMPANY_BASENAME}/${companySlug}${NEWSFEED_PAGE_ROUTE.path}#${ARTICLE_TITLE_ID}`}
          className={styles.backLink}
        >
          {t("companies_client.newsFeed.articles.details.backLink")}
        </BackLink>
        <div className={styles.tagWrapper}>
          {targeted && (
            <Signature imgSrc={dataCareerCenter?.logo || ""} imgAlt="" className={styles.signature}>
              {t("companies_client.newsFeed.articles.exclusive", { name: dataCareerCenter?.name || "" })}
            </Signature>
          )}
          <Tag size="small">{t(`companies_client.articles_type.${ARTICLE_SUBJECT_TYPES_I18N[subject]}`)}</Tag>
        </div>
        <div className={styles.articleHeadWrapper}>
          <div className={styles.titleWrapper}>
            <Heading variant="title2" weight="bold" as="h2">
              {title}
            </Heading>
            <Text variant="body2" as="div" className={styles.articleDate}>
              {getDate({
                date: new Date(createTime),
                dateFormat: DATE_FORMATS.LIGHT_DATE,
                locale,
                timeZone: "Europe/Paris",
              })}
            </Text>
          </div>
          {isRecruitmentArticleWithJobs && (
            <Button
              renderComponent={Link}
              href={`/${locale}/companies/${companySlug}/job-offers`}
              variant="highlight"
              icon={Briefcase}
              onClick={() => handleClick("top")}
            >
              {t("companies_client.newsFeed.see_jobads_cta", { count: nbJobAds })}
            </Button>
          )}
        </div>
        {hasVideo && (
          <VideoDynamic
            video={video}
            withDescription={false}
            className={styles.articleVideo}
            tracking={{ id: dataCompany?.id, name: dataCompany?.page.name }}
          />
        )}
        {hasImage && <ImageDynamic src={images[0].url} alt={images[0]?.alt || ""} />}
        <RichText tag="div" className={styles.richText} allowTargetAttribute>
          {content}
        </RichText>
        {externUrl && (
          <LinkUiKit href={externUrl} target="_blank" rel="noopener noreferrer" className={styles.link}>
            {externUrl}
          </LinkUiKit>
        )}
        {hasGallery && (
          <ElementGrid className={styles.grid}>
            {images.map(image => {
              return (
                <Image
                  key={image.url}
                  alt={image.alt || ""}
                  src={image.url}
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 250px"
                  width="16"
                  height="9"
                  className={styles.image}
                  role="button"
                  onClick={() => {
                    openModal(image.url)
                  }}
                />
              )
            })}
          </ElementGrid>
        )}
        <Modal onClose={() => closeModal()} isOpen={isModalOpen} wrapperClassName={styles.modalWrapper}>
          <Modal.Header className={styles.modalHeader}>
            <Modal.Title> </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Image src={modalImageSrc} alt="" width="1600" height="900" className={styles.modalImage} />
          </Modal.Body>
        </Modal>
        {isRecruitmentArticleWithJobs && (
          <div className={styles.footerCtaWrapper}>
            <Heading variant="title3" weight="bold" as="h3">
              {t("companies_client.newsFeed.article.footer_banner.title", { company: dataCompany?.page.name || "" })}
            </Heading>
            <Text variant="body2">{t("companies_client.newsFeed.article.footer_banner.subtext")}</Text>
            <Button
              renderComponent={Link}
              href={`/${locale}/companies/${companySlug}/job-offers`}
              variant="highlight"
              icon={Briefcase}
              onClick={() => handleClick("bottom")}
            >
              {t("companies_client.newsFeed.see_jobads_cta", { count: nbJobAds })}
            </Button>
          </div>
        )}
      </Layout.Column>
    </Layout>
  )
}
